import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { truncate } from '@/utils/truncate'
import { useAppSelector, useAppDispatch } from '../../../../../store/hooks.ts'
import { selectOpenDropdownId } from '../../../../../store/ui/uiSelectors.ts'
import { setOpenDropdown, setOpenModal } from '../../../../../store/ui/uiSlice.ts'
import type { UserWorkspace } from '../../../../../store/auth/authSlice.ts'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext.tsx'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper.tsx'
import Dropdown from '../../../../ui/Dropdown/Dropdown.tsx'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem.tsx'
import DropdownDivider from '@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider.tsx'
import WorkspaceAvatar from '@/components/ui/WorkspaceAvatar/WorkspaceAvatar'
import Badge from '@/components/ui/Badge/Badge'
import IconChevron from '@/assets/icons/icon-arrow-down.svg?react'
import { selectIsWorkspacePro } from '../../../../../store/workspace/workspaceSelectors'
import css from './SidebarWorkspaceButton.module.css'
import {TextNorm} from "../../../../ui/Text";

const DROPDOWN_ID = 'sidebar-workspace-button'

const SidebarWorkspaceButton = () => {
    const dispatch = useAppDispatch()
    const openDropdownId = useAppSelector(selectOpenDropdownId)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)

    const { workspace: workspaceUrlId } = useParams<{ workspace: string }>()
    const workspaces: UserWorkspace[] = useAppSelector(state => state.auth?.workspaces ?? [])
    const currentUserId = useAppSelector(state => state.auth?.id)
    const loginSection = useAppSelector(state => state.auth?.loginSection ?? 'projects')
    const { workspace: currentWorkspace, members } = useWorkspace()
    const isPro = useAppSelector(selectIsWorkspacePro)

    // The shell now paints before the workspace doc loads (the splash drops on auth + the
    // workspace list). Until the doc arrives, render the button face from the cached
    // auth.workspaces entry so the name/avatar don't pop in a beat later. Once the live doc
    // is present we read every field off it (never mix in the cached avatar — the doc's null
    // avatar means "removed", which must win over a stale cached URL).
    const fallbackWorkspace = workspaces.find(ws => ws.workspaceUrlId === workspaceUrlId)
    const workspaceFace = currentWorkspace ?? fallbackWorkspace
    const displayName = workspaceFace?.name ?? ''
    const displayAvatar = workspaceFace?.avatar ?? null
    const hasWorkspaceFace = Boolean(workspaceFace)

    const otherWorkspaces = workspaces
        .filter(ws => ws.id !== currentWorkspace?.id)
        .sort((a, b) => a.name.localeCompare(b.name))
    const isOwner = members.find(m => m.userId === currentUserId)?.role === 'owner'

    useEffect(() => {
        const shouldBeOpen = openDropdownId === DROPDOWN_ID
        if (isDropdownOpen !== shouldBeOpen) {
            setIsDropdownOpen(shouldBeOpen)
        }
    }, [openDropdownId, isDropdownOpen])

    const handleClick = () => {
        dispatch(setOpenDropdown(isDropdownOpen ? null : DROPDOWN_ID))
    }

    const handleClose = () => {
        dispatch(setOpenDropdown(null))
    }

    const sectionPaths: Record<string, string> = {
        'my-tasks': '/my-tasks',
        'team': '/team',
        'all-tasks': '/all-tasks',
        'projects': '/projects',
    }

    const handleWorkspaceClick = (workspaceUrlId: string) => {
        dispatch(setOpenDropdown(null))
        const sectionPath = sectionPaths[loginSection] ?? '/projects'
        window.location.href = `/${workspaceUrlId}${sectionPath}`
    }

    const handleAddWorkspace = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'createWorkspace', canClose: true }))
    }

    const handleSettings = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'projectSettings' }))
    }

    const handleMembers = () => {
        dispatch(setOpenDropdown(null))
        dispatch(setOpenModal({ type: 'projectSettings', initialTab: 'members' }))
    }

    const truncatedName = truncate(currentWorkspace?.name || '', 20)

    return (
        <DropdownWrapper>
            <div className={css.button} onClick={handleClick}>
                {hasWorkspaceFace && (
                    <WorkspaceAvatar
                        name={displayName}
                        avatar={displayAvatar}
                        size="medium"
                        className={css.icon}
                    />
                )}
                <TextNorm className={css.title}>{displayName}</TextNorm>
                <IconChevron className={css.chevron} />
            </div>
            <Dropdown isOpen={isDropdownOpen} onClose={handleClose}>
                {currentWorkspace && (
                    <>
                        <DropdownItem
                            highlight={true}
                            title={truncatedName}
                            rightIcon={<Badge>{isPro ? 'Pro' : 'Free'}</Badge>}
                            onClick={() => handleWorkspaceClick(currentWorkspace.workspaceUrlId)}
                        />
                        {isOwner && (
                            <>
                                <DropdownItem
                                    title="Settings"
                                    onClick={handleSettings}
                                />
                                <DropdownItem
                                    title="Members"
                                    rightTitle={String(members.filter(m => m.status === 'active').length)}
                                    onClick={handleMembers}
                                />
                            </>
                        )}
                    </>
                )}
                <DropdownDivider />
                {otherWorkspaces.map((ws) => (
                    <DropdownItem
                        key={ws.id}
                        title={truncate(ws.name, 20)}
                        onClick={() => handleWorkspaceClick(ws.workspaceUrlId)}
                    />
                ))}
                <DropdownItem
                    muted={true}
                    title="Add workspace"
                    onClick={handleAddWorkspace}
                />
            </Dropdown>
        </DropdownWrapper>
    )
}

export default SidebarWorkspaceButton
